// Sample contacts used by the /contacts demo/empty state. Shape mirrors the
// /get-contacts response so the real <ContactCard /> renders them unchanged.
// `id` is the array index (matching the real fetch's .map((c, i) => ...)).
//
// Nothing here ever persists: while these render, useDemoNetworkGuard keeps
// window.__demoMode on, so any action (tag, AI/Me toggle, etc.) updates local
// state only and is blocked from reaching the backend.

import { DEMO_LEAD_COMPONENTS } from "./demoNeedsReviewData";

const now = Math.floor(Date.now() / 1000);
const daysAgo = (d) => now - d * 86400;

function notif(property, market) {
  return {
    user_settings: {
      notifications: {
        property_updates: { enabled: property },
        market_updates: { enabled: market },
      },
    },
  };
}

const DEMO_CONTACTS = [
  {
    first_name: "Sarah",
    last_name: "Mitchell",
    email: "sarah.mitchell@example.com",
    clean_mobile: "0412 345 678",
    suburb: "Balmain",
    state: "NSW",
    residence_address: "14 Darling Street, Balmain NSW 2041",
    source: "appraisal",
    lead_score: 92,
    lead_last_event_at: daysAgo(0),
    handover_notes_count: 2,
    ai_owns_relationship: true,
    tags: [
      { slug: "hot-lead", label: "Hot lead", color: "#ef4444", category: "status", source: "ai" },
      { slug: "seller", label: "Seller", color: "#0d9488", category: "intent", source: "human" },
    ],
    ...notif(true, true),
  },
  {
    first_name: "James",
    last_name: "Nguyen",
    email: "james.nguyen@example.com",
    clean_mobile: "0423 887 210",
    suburb: "Hawthorn",
    state: "VIC",
    residence_address: "8 Glenferrie Road, Hawthorn VIC 3122",
    source: "property_report",
    lead_score: 68,
    lead_last_event_at: daysAgo(2),
    handover_notes_count: 0,
    ai_owns_relationship: true,
    tags: [
      { slug: "nurturing", label: "Nurturing", color: "#f59e0b", category: "status", source: "ai" },
    ],
    ...notif(true, false),
  },
  {
    first_name: "Priya",
    last_name: "Sharma",
    email: "priya.sharma@example.com",
    clean_mobile: "0438 552 901",
    suburb: "Toowong",
    state: "QLD",
    residence_address: "22 Sherwood Road, Toowong QLD 4066",
    source: "csv_upload",
    lead_score: 54,
    lead_last_event_at: daysAgo(5),
    handover_notes_count: 1,
    ai_owns_relationship: false,
    tags: [
      { slug: "buyer", label: "Buyer", color: "#3b82f6", category: "intent", source: "human" },
    ],
    ...notif(false, true),
  },
  {
    first_name: "Tom",
    last_name: "Andersson",
    email: "tom.andersson@example.com",
    clean_mobile: "0401 229 384",
    suburb: "Glenelg",
    state: "SA",
    residence_address: "5 Moseley Street, Glenelg SA 5045",
    source: "appraisal",
    lead_score: 41,
    lead_last_event_at: daysAgo(9),
    handover_notes_count: 0,
    ai_owns_relationship: true,
    tags: [],
    ...notif(false, false),
  },
  {
    first_name: "Olivia",
    last_name: "Brown",
    email: "olivia.brown@example.com",
    clean_mobile: "0455 671 023",
    suburb: "Subiaco",
    state: "WA",
    residence_address: "31 Rokeby Road, Subiaco WA 6008",
    source: "referral",
    lead_score: 23,
    lead_last_event_at: daysAgo(18),
    handover_notes_count: 0,
    ai_owns_relationship: false,
    tags: [
      { slug: "past-client", label: "Past client", color: "#8b5cf6", category: "relationship", source: "human" },
    ],
    ...notif(true, true),
  },
  {
    first_name: "Daniel",
    last_name: "Lee",
    email: "daniel.lee@example.com",
    clean_mobile: "0467 318 552",
    suburb: "Newtown",
    state: "NSW",
    residence_address: "97 King Street, Newtown NSW 2042",
    source: "property_report",
    lead_score: 12,
    lead_last_event_at: daysAgo(34),
    handover_notes_count: 0,
    ai_owns_relationship: true,
    tags: [],
    ...notif(false, false),
  },
].map((c, i) => ({
  ...c,
  id: i,
  lead_components: DEMO_LEAD_COMPONENTS[c.email] || null,
}));

// System tag catalogue surfaced by the "+ Tag" picker while the /contacts
// sample view is on screen. Shape mirrors /contact-tags/list-definitions so
// ContactTagPicker renders it unchanged — grouped into the system tabs it
// orders by (intent · lifecycle · persona · admin). The slugs/colours applied
// to the demo rows above (hot-lead, seller, nurturing, buyer, past-client) are
// included so they show as already-applied when a row's picker opens.
export const DEMO_TAG_DEFINITIONS = [
  // intent — what the contact wants to do
  { id: "tag-seller", slug: "seller", label: "Seller", color: "#0d9488", category: "intent", description: "Looking to sell a property", is_system: true },
  { id: "tag-buyer", slug: "buyer", label: "Buyer", color: "#3b82f6", category: "intent", description: "Actively searching to buy", is_system: true },
  { id: "tag-investor", slug: "investor", label: "Investor", color: "#6366f1", category: "intent", description: "Buying for investment or yield", is_system: true },
  { id: "tag-landlord", slug: "landlord", label: "Landlord", color: "#0ea5e9", category: "intent", description: "Owns one or more rental properties", is_system: true },
  { id: "tag-downsizer", slug: "downsizer", label: "Downsizer", color: "#f97316", category: "intent", description: "Looking to move to a smaller home", is_system: true },
  // lifecycle — where they are in the funnel
  { id: "tag-new-lead", slug: "new-lead", label: "New lead", color: "#38bdf8", category: "lifecycle", description: "Recently entered your database", is_system: true },
  { id: "tag-hot-lead", slug: "hot-lead", label: "Hot lead", color: "#ef4444", category: "lifecycle", description: "High intent — follow up now", is_system: true },
  { id: "tag-nurturing", slug: "nurturing", label: "Nurturing", color: "#f59e0b", category: "lifecycle", description: "Long-term — keep warm with updates", is_system: true },
  { id: "tag-appraisal-booked", slug: "appraisal-booked", label: "Appraisal booked", color: "#8b5cf6", category: "lifecycle", description: "Appraisal appointment scheduled", is_system: true },
  { id: "tag-listing-won", slug: "listing-won", label: "Listing won", color: "#16a34a", category: "lifecycle", description: "Signed a listing authority", is_system: true },
  { id: "tag-past-client", slug: "past-client", label: "Past client", color: "#8b5cf6", category: "lifecycle", description: "Has bought or sold with you before", is_system: true },
  { id: "tag-lost", slug: "lost", label: "Lost", color: "#9ca3af", category: "lifecycle", description: "Went cold or listed elsewhere", is_system: true },
  // persona — who they are
  { id: "tag-first-home-buyer", slug: "first-home-buyer", label: "First home buyer", color: "#06b6d4", category: "persona", description: "Buying their first home", is_system: true },
  { id: "tag-upsizer", slug: "upsizer", label: "Upsizer", color: "#22c55e", category: "persona", description: "Moving to a larger home", is_system: true },
  { id: "tag-empty-nester", slug: "empty-nester", label: "Empty nester", color: "#d946ef", category: "persona", description: "Children have moved out", is_system: true },
  { id: "tag-developer", slug: "developer", label: "Developer", color: "#7c3aed", category: "persona", description: "Buys to develop or subdivide", is_system: true },
  // admin — operational flags
  { id: "tag-vip", slug: "vip", label: "VIP", color: "#eab308", category: "admin", description: "Priority relationship", is_system: true },
  { id: "tag-do-not-contact", slug: "do-not-contact", label: "Do not contact", color: "#dc2626", category: "admin", description: "Opted out — do not email or call", is_system: true },
  { id: "tag-duplicate", slug: "duplicate", label: "Duplicate", color: "#94a3b8", category: "admin", description: "Possible duplicate record", is_system: true },
];

export default DEMO_CONTACTS;
